import Dropzone from 'react-dropzone';

import type { DropEvent } from 'react-dropzone';

/** What a drop of folders holds: each folder path, and each file with the path of its folder. CLOUD-1082. */
export type DroppedTree = {
  folders: string[][];
  files: Array<{ file: File; path: string[] }>;
};

// The browser adds these to a dropped folder. Nobody means to upload them.
const SYSTEM_FILES = new Set(['.DS_Store', 'Thumbs.db', 'desktop.ini']);

/**
 * The dropped entries, when at least one is a folder. Read them during the drop event:
 * the browser empties the item list after it.
 */
export function droppedFolderEntries(items: DataTransferItemList | null | undefined): FileSystemEntry[] | null {
  if (!items) return null;
  const entries = Array.from(items)
    .filter((item) => item.kind === 'file')
    .map((item) => item.webkitGetAsEntry?.() ?? null)
    .filter((entry): entry is FileSystemEntry => entry !== null);
  return entries.some((entry) => entry.isDirectory) ? entries : null;
}

/** A reader gives a long folder in parts, so it is read until it gives nothing. */
async function childrenOf(entry: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  const reader = entry.createReader();
  const children: FileSystemEntry[] = [];
  for (;;) {
    const part = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
    if (part.length === 0) return children;
    children.push(...part);
  }
}

async function folderPaths(entry: FileSystemEntry, path: string[], into: string[][]): Promise<void> {
  if (!entry.isDirectory) return;
  const folderPath = [...path, entry.name];
  into.push(folderPath);
  for (const child of await childrenOf(entry as FileSystemDirectoryEntry)) await folderPaths(child, folderPath, into);
}

/** The folder path of a dropped file. The dropzone gives each file its path in the drop, as "/Reports/a.pdf". */
function folderOf(file: File): string[] {
  const { relativePath } = file as File & { relativePath?: string };
  const parts = (relativePath ?? file.name).split('/').filter((part) => part !== '' && part !== '.');
  return parts.slice(0, -1);
}

/**
 * The folders come from the entries, so an empty folder is made too. The files come from the dropzone,
 * because it fills in the type that the browser leaves empty for many files.
 */
export async function readDroppedTree(entries: FileSystemEntry[], files: File[]): Promise<DroppedTree> {
  const folders: string[][] = [];
  for (const entry of entries) await folderPaths(entry, [], folders);
  return {
    folders,
    files: files.filter((file) => !SYSTEM_FILES.has(file.name)).map((file) => ({ file, path: folderOf(file) })),
  };
}

/** How the dropzone reads the files of an event. */
export type ReadDropFiles = (event: DropEvent) => Promise<Array<File | DataTransferItem>>;

/**
 * The dropzone's own reader. It fills in the type that the browser leaves empty for many files.
 * The library does not export it by name, so it comes from the defaults of its component.
 */
export const dropzoneReader = (Dropzone as unknown as { defaultProps: { getFilesFromEvent: ReadDropFiles } })
  .defaultProps.getFilesFromEvent;

/** The dropped files that are not in a folder. Read them during the drop event, as the folder entries. */
function looseDroppedFiles(items: DataTransferItemList | null | undefined): File[] {
  if (!items) return [];
  return Array.from(items)
    .filter((item) => item.kind === 'file' && !item.webkitGetAsEntry?.()?.isDirectory)
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);
}

/** What a failed drop left out: its folders, or plain files the browser did not give. */
export type UnreadPart = 'folders' | 'files';

/** The dropzone skips onDrop when its reader fails, so a failed drop gives its readable loose files instead. */
export async function readDropFiles(
  event: DropEvent,
  read: ReadDropFiles,
  onUnread: (part: UnreadPart) => void
): Promise<Array<File | DataTransferItem>> {
  const isDrop = 'type' in event && event.type === 'drop';
  const items = isDrop ? (event as DragEvent).dataTransfer?.items : null;
  const loose = looseDroppedFiles(items);
  const hasFolder = droppedFolderEntries(items) !== null;
  const ungiven = Array.from(items ?? []).some(
    (item) => item.kind === 'file' && !item.webkitGetAsEntry?.()?.isDirectory && item.getAsFile() === null
  );
  try {
    return await read(event);
  } catch (error) {
    if (!isDrop) throw error;
    if (hasFolder) onUnread('folders');
    else if (ungiven) onUnread('files');
    // As the files of a file input, so the reader fills in their type. The reader marks each file it
    // reads and cannot mark one twice, so it gets new File objects. They hold the same data.
    const files = loose.map(
      (file) => new File([file], file.name, { type: file.type, lastModified: file.lastModified })
    );
    return read({ target: { files } } as unknown as DropEvent);
  }
}
